import React from "react";
import { Navigate, Routes, Route } from "react-router-dom";
import PublicEnquiryFormPage from "../features/enquiries/pages/PublicEnquiryFormPage";
import PreconsultationFormPage from "../features/preconsultation/pages/PreconsultationFormPage";
import { FORM_PATHS, formHandleFromHost } from "../lib/site/config";

/**
 * The whole of the forms site — a practitioner's own subdomain.
 *
 * `*.naurist.com` and `*.naurist.openuidev.com` are served by the same server,
 * off the same bundle, as the dashboard: which of the two products answers is
 * decided from the hostname, and this table is what a form host mounts. See
 * `lib/site/config`.
 *
 * Two forms, and both are reached by a fixed path, so nothing on this domain
 * has to be guessed at:
 *
 *   - `rohan.naurist.com/form` — the practitioner's public enquiry form. Whose
 *     form it is comes from the subdomain, never from the path.
 *   - `rohan.naurist.com/i/<token>` — the pre-consultation form, which is
 *     private and single-use, so it is reached by a token the backend minted
 *     for one person.
 *
 * Static segments outrank a dynamic one in the router, so `/form` and `/i` are
 * never read as somebody's name; `form` is reserved at the point a name is
 * claimed all the same — see `RESERVED_LINK_NAMES`.
 *
 * ## Two tables, chosen by whether the hostname names anybody
 *
 * On a practitioner's own subdomain the hostname has already said whose form
 * this is, so no path may say otherwise and no path means anything else:
 * everything that is not one of the two routes above is **redirected** to
 * `/form` rather than rendered there.
 *
 * Rendering it in place was the wrong call, and it showed. Somebody landing on
 * `rohan.naurist.com/login` — which is where a stale bundle sends them, having
 * mistaken the host for the dashboard's — got Rohan's enquiry form drawn under
 * an address that says `login`. The form was right and the address bar was
 * lying, which is the worse half to get wrong: it is what gets copied, shared,
 * and reported as a bug. Normalising the URL makes the recovery visible.
 *
 * On a shared host — `connect.naurist.com`, or a developer on localhost — the
 * hostname names nobody, so the first segment is the only place a name can
 * come from and `/:handle` is what reads it. An unclaimed one falls through to
 * the same "not available" notice as everything else.
 *
 * The dashboard cannot be reached here either way: its screens are not
 * registered on this table, and this table is the only one a form host mounts.
 *
 * Registered outside `AppRoutes` on purpose. That file sits inside Clerk, the
 * toast provider and the query client, and nothing on this domain has any of
 * them: the person here is not a Naurist user and never will be.
 */
const FormsRoutes: React.FC = () => {
  // Whose subdomain this is, or `""` on a shared host. Read once per render
  // rather than per route, since it cannot change without a reload.
  const named = !!formHandleFromHost();

  return (
    <Routes>
      <Route path={FORM_PATHS.enquiry} element={<PublicEnquiryFormPage />} />
      <Route
        path={`${FORM_PATHS.preconsultation}/:token`}
        element={<PreconsultationFormPage />}
      />
      {named ? (
        // Including `/` — the bare address the backend handed out before there
        // was a path, which stays a working link by landing on the real one.
        <Route
          path="*"
          element={<Navigate to={FORM_PATHS.enquiry} replace />}
        />
      ) : (
        <>
          <Route path="/" element={<Navigate to={FORM_PATHS.enquiry} replace />} />
          <Route path="/:handle" element={<PublicEnquiryFormPage />} />
          {/* A deeper URL — a link that picked up a trailing path somewhere —
              lands on the "not available" notice rather than a blank page. */}
          <Route path="*" element={<PublicEnquiryFormPage />} />
        </>
      )}
    </Routes>
  );
};

export default FormsRoutes;
